import { cva, type VariantProps } from "class-variance-authority";
import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { commissionBadgeText } from "@/lib/commission";
import { cn } from "@/lib/utils";

const statusBadgeVariants = cva("text-xs capitalize border", {
  variants: {
    tone: {
      neutral: "bg-muted text-muted-foreground border-border",
      success: "bg-success-muted text-success border-success/20",
      warning: "bg-warning-muted text-warning border-warning/20",
      destructive:
        "bg-destructive-muted text-destructive border-destructive/20",
    },
  },
  defaultVariants: { tone: "neutral" },
});

type Tone = NonNullable<VariantProps<typeof statusBadgeVariants>["tone"]>;

export type ConnectionStatus =
  | "invited"
  | "accepted"
  | "declined"
  | "disconnected";
// `cancelled` = auto-renew off but paid access still active until
// subscription_expires_at. `expired` = access has truly ended.
export type SubscriptionStatus =
  | "free"
  | "subscribed"
  | "cancelled"
  | "expired";
export type CommissionStatus = "-" | "earning" | "earned" | "expired";

const CONNECTION_TONE: Record<ConnectionStatus, Tone> = {
  invited: "neutral",
  accepted: "success",
  declined: "destructive",
  disconnected: "neutral",
};

const SUBSCRIPTION_TONE: Record<SubscriptionStatus, Tone> = {
  free: "neutral",
  subscribed: "success",
  cancelled: "warning",
  expired: "destructive",
};

const COMMISSION_TONE: Record<CommissionStatus, Tone> = {
  "-": "neutral",
  earning: "warning",
  earned: "success",
  expired: "destructive",
};

const CONNECTION_TOOLTIP: Record<ConnectionStatus, string> = {
  invited: "Invite sent. Client hasn't accepted yet.",
  accepted: "Client connected their Taxr account to you.",
  declined: "Client declined your invitation.",
  disconnected: "Connection removed by either side.",
};

function formatDate(iso?: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

function subscriptionTooltip(
  status: SubscriptionStatus,
  expiresAt?: string | null,
): string {
  switch (status) {
    case "free":
      return "Client is on the free tier, no paid subscription yet.";
    case "subscribed":
      return "Client has an active paid subscription with auto-renew on.";
    case "cancelled": {
      const when = formatDate(expiresAt);
      return when
        ? `Auto-renew is off. Access continues until ${when}.`
        : "Auto-renew is off. Access continues until the current period ends.";
    }
    case "expired":
      return "Subscription has ended. Client needs to resubscribe to sync new receipts.";
  }
}

export function ConnectionBadge({ status }: { status: ConnectionStatus }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            variant="outline"
            className={cn(
              statusBadgeVariants({ tone: CONNECTION_TONE[status] }),
              "cursor-help",
            )}
          >
            {status}
          </Badge>
        }
      />
      <TooltipContent side="top" className="max-w-[240px] text-center">
        {CONNECTION_TOOLTIP[status]}
      </TooltipContent>
    </Tooltip>
  );
}

export function SubscriptionBadge({
  status,
  expiresAt,
}: {
  status: SubscriptionStatus;
  expiresAt?: string | null;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            variant="outline"
            className={cn(
              statusBadgeVariants({ tone: SUBSCRIPTION_TONE[status] }),
              "cursor-help",
            )}
          >
            {status}
          </Badge>
        }
      />
      <TooltipContent side="top" className="max-w-[260px] text-center">
        {subscriptionTooltip(status, expiresAt)}
      </TooltipContent>
    </Tooltip>
  );
}

export function CommissionBadge({
  status,
  connectionStatus,
  subscriptionStatus,
  clientInitiated = false,
  noTooltip = false,
}: {
  status: CommissionStatus;
  connectionStatus?: ConnectionStatus;
  /** Tells "not yet subscribed" apart from a subscriber who earns nothing. */
  subscriptionStatus?: SubscriptionStatus;
  /** The client added this practice from the app: never earns. */
  clientInitiated?: boolean;
  /** Skip the hover tooltip — used on the clients table where the
   *  commission cell is inside a click target, so the pointer cue
   *  plus the button's own affordance carry the meaning. */
  noTooltip?: boolean;
}) {
  const { label, tooltip } = commissionBadgeText({
    status,
    connectionStatus,
    subscriptionStatus,
    clientInitiated,
  });
  const tone: Tone =
    status === "-" && connectionStatus === "declined"
      ? "destructive"
      : COMMISSION_TONE[status];

  if (noTooltip) {
    return (
      <Badge
        variant="outline"
        className={cn(statusBadgeVariants({ tone }))}
      >
        {label}
      </Badge>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <Badge
            variant="outline"
            className={cn(statusBadgeVariants({ tone }), "cursor-help")}
          >
            {label}
          </Badge>
        }
      />
      <TooltipContent side="top" className="max-w-[260px] text-center">
        {tooltip}
      </TooltipContent>
    </Tooltip>
  );
}

export { statusBadgeVariants };
